import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Shield, User, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BackgroundCheckData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  background_check_approved: boolean;
}

interface BackgroundCheckManagerProps {
  mentorId?: string;
  isAdminView?: boolean;
}

export function BackgroundCheckManager({ mentorId, isAdminView = false }: BackgroundCheckManagerProps) {
  const [mentors, setMentors] = useState<BackgroundCheckData[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchBackgroundCheckData();
  }, [mentorId, isAdminView]);

  const fetchBackgroundCheckData = async () => {
    try {
      // Query mentors table for basic info and join with mentor_compliance for background check status
      let mentorQuery = supabase
        .from('mentors')
        .select(`
          id,
          first_name,
          last_name,
          email
        `);

      if (mentorId && !isAdminView) {
        mentorQuery = mentorQuery.eq('id', mentorId);
      }

      const { data: mentorsData, error: mentorsError } = await mentorQuery;
      if (mentorsError) throw mentorsError;

      if (!mentorsData || mentorsData.length === 0) {
        setMentors([]);
        return;
      }

      // Get compliance data for these mentors
      const mentorIds = mentorsData.map(m => m.id);
      const { data: complianceData, error: complianceError } = await supabase
        .from('mentor_compliance')
        .select(`
          mentor_id,
          background_check_approved
        `)
        .in('mentor_id', mentorIds);

      if (complianceError) throw complianceError;

      // Combine mentor and compliance data
      const combinedData = mentorsData.map(mentor => {
        const compliance = complianceData?.find(c => c.mentor_id === mentor.id);
        return {
          ...mentor,
          background_check_approved: compliance?.background_check_approved || false
        };
      });

      setMentors(combinedData);
    } catch (error: any) {
      console.error('Error fetching background check data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load background check data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateBackgroundCheckStatus = async (mentorId: string, approved: boolean) => {
    try {
      setUpdating(mentorId);

      // Update the protected mentor_compliance table
      const { error } = await supabase
        .from('mentor_compliance')
        .upsert({
          mentor_id: mentorId,
          background_check_approved: approved
        }, {
          onConflict: 'mentor_id'
        });

      if (error) throw error;

      await fetchBackgroundCheckData();

      toast({
        title: 'Success',
        description: `Background check ${approved ? 'approved' : 'revoked'} successfully`
      });
    } catch (error: any) {
      console.error('Error updating background check:', error);
      toast({
        title: 'Error',
        description: 'Failed to update background check status',
        variant: 'destructive'
      });
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading background check data...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            {isAdminView ? 'Background Check Management' : 'Your Background Check Status'}
          </CardTitle>
          <CardDescription>
            {isAdminView 
              ? 'Manage mentor background check approvals for in-person sessions'
              : 'Background check approval is required for in-person mentoring sessions'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mentors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {isAdminView 
                ? 'No mentors requiring background check management'
                : 'No background check data found'
              }
            </div>
          ) : (
            <div className="space-y-4">
              {mentors.map((mentor) => (
                <div key={mentor.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <h3 className="font-medium">{mentor.first_name} {mentor.last_name}</h3>
                          <p className="text-sm text-muted-foreground">{mentor.email}</p>
                        </div>
                      </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge variant={mentor.background_check_approved ? 'default' : 'secondary'}>
                        {mentor.background_check_approved ? (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approved
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" />
                            Not Approved
                          </>
                        )}
                      </Badge>
                      
                      {isAdminView && (
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`bg-check-${mentor.id}`} className="text-sm">
                            Approved
                          </Label>
                          <Switch
                            id={`bg-check-${mentor.id}`}
                            checked={mentor.background_check_approved}
                            onCheckedChange={(checked) => updateBackgroundCheckStatus(mentor.id, checked)}
                            disabled={updating === mentor.id}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {!isAdminView && (
                    <div className="mt-3 p-3 bg-muted rounded-md">
                      <p className="text-sm text-muted-foreground">
                        {mentor.background_check_approved
                          ? 'Your background check has been approved. You can offer in-person sessions.'
                          : 'Background check approval is managed externally. Contact support if you need assistance.'
                        }
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}